import { Link } from 'react-router';
import { buildClaimPackFacts } from '@fixtyer/shared';
import { BreadcrumbSchema, SEO } from '@/components/seo/seo';

const sampleFacts = buildClaimPackFacts({
  claimantName: 'Alex Morgan [REDACTED]',
  incidentDate: '14 February 2026',
  incidentTime: '10:30',
  locationAddress: 'Example Road [REDACTED]',
  locationPostcode: 'EX1 1AA',
  vehicleReg: '[REDACTED]',
  vehicleMakeModel: 'Example vehicle [REDACTED]',
  damageDescription: 'punctured tyre and wheel damage [REDACTED]',
  hasDefectPhotos: true,
  hasDamagePhotos: true,
  hasRepairInvoice: false,
  hasRepairQuote: true,
});

const previewPages = [
  {
    title: 'Formal Claim Letter',
    body: [
      'Claimant: Alex Morgan [REDACTED]',
      'Incident: 14 February 2026, Example Road [REDACTED]',
      'Authority: Example County Council',
      'Summary: The vehicle encountered a reported carriageway defect. The claimant asks the authority to review the enclosed facts and evidence.',
    ],
  },
  {
    title: sampleFacts.witnessTitle,
    body: [
      sampleFacts.statementOfTruthDirection,
      ...sampleFacts.witnessParagraphs,
      'Signature: ____________________   Date: __________',
    ],
  },
  {
    title: sampleFacts.scheduleTitle,
    body: [
      ...sampleFacts.scheduleEntries,
      'This chronology is prepared from the information supplied by the customer.',
    ],
  },
  {
    title: 'Organised Evidence Bundle',
    body: [
      'A1 — incident-location photograph [REDACTED]',
      'A2 — highway-defect photograph with customer measurement',
      'B1 — vehicle-damage photograph [REDACTED]',
      'C1 — repair quotation or invoice [REDACTED]',
    ],
  },
  {
    title: 'Authority-name Submission Guidance',
    body: [
      '1. Review every document and attachment for accuracy.',
      '2. Correct or remove anything inaccurate; sign only when every fact is accurate.',
      '3. Send the submission documents by recorded delivery to Example County Council Claims Department.',
      '4. Keep copies and manage correspondence or requests from the authority.',
    ],
  },
];

export function SampleCouncilClaimPackPage() {
  return (
    <>
      <SEO
        title="Sample Council Claim Pack"
        description="Explore an illustrative synthetic preview of Fixtyer's current £35 Council Pack deliverables."
        canonical="/sample-council-claim-pack"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'Sample Council Claim Pack', url: '/sample-council-claim-pack' },
      ]} />
      <main className="mx-auto max-w-6xl px-4 py-12">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-bold uppercase tracking-wide text-accent-700">
            Illustrative synthetic sample—not a real customer or outcome
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold text-brand-950 sm:text-5xl">
            See what the £35 Council Pack contains
          </h1>
          <p className="mt-5 text-lg text-surface-700">
            A realistic redacted demonstration based on the current Council Pack
            generator. It demonstrates document organisation, not a legal or financial
            outcome.
          </p>
          <button
            type="button"
            onClick={() => window.print()}
            className="mt-6 rounded-lg border border-brand-300 px-5 py-3 font-semibold text-brand-800 print:hidden"
          >
            Print or save as PDF
          </button>
        </div>

        <section className="mt-12 grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="space-y-8">
            {previewPages.map((page, index) => (
              <article
                key={page.title}
                data-testid="sample-pack-page"
                className="min-h-[32rem] rounded-lg border border-surface-300 bg-white p-8 shadow-lg print:break-after-page print:shadow-none"
              >
                <div className="flex items-center justify-between border-b border-surface-200 pb-4">
                  <p className="font-display font-bold text-brand-950">FIXTYER COUNCIL CLAIM PACK</p>
                  <p className="text-xs text-surface-500">Page {index + 1} of {previewPages.length}</p>
                </div>
                <h2 className="mt-8 font-display text-2xl font-bold text-brand-950">
                  {page.title}
                </h2>
                <div className="mt-6 space-y-5 text-surface-800">
                  {page.body.map((paragraph) => (
                    <p key={paragraph} className="border-b border-surface-100 pb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>
                <p className="mt-8 text-xs font-semibold uppercase tracking-wide text-accent-700">
                  Illustrative synthetic sample—not a real customer or outcome
                </p>
              </article>
            ))}
          </div>

          <aside className="space-y-6 lg:sticky lg:top-6 lg:self-start">
            <section className="rounded-2xl border border-surface-200 bg-white p-5">
              <h2 className="font-display text-xl font-semibold text-brand-950">
                Current deliverables
              </h2>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-surface-700">
                <li>Formal claim letter</li>
                <li>Draft witness statement with Statement of Truth</li>
                <li>Draft schedule of facts</li>
                <li>Organised evidence bundle</li>
                <li>Authority-name submission guidance</li>
                <li>Prior-report context only when available</li>
              </ul>
            </section>
            <section className="rounded-2xl border border-surface-200 bg-white p-5 text-sm text-surface-700">
              <h2 className="font-display text-xl font-semibold text-brand-950">
                What you still manage
              </h2>
              <p className="mt-3">
                The customer must review every fact, correct inaccuracies, sign only when
                the Statement of Truth is accurate, send the
                submission documents by recorded delivery to the Claims Department named
                in the pack, meet deadlines and manage correspondence or disputes.
              </p>
            </section>
            <section className="rounded-2xl bg-brand-950 p-5 text-white">
              <p className="font-display text-2xl font-bold">£35</p>
              <p className="mt-1">Fixed price. No commission.</p>
              <p className="mt-3 text-sm text-brand-200">
                The Court Pack is planned and is not currently sold.
              </p>
              <Link
                to="/check"
                className="mt-4 inline-block rounded-lg bg-accent-500 px-4 py-2 font-semibold text-white"
              >
                Start the free check
              </Link>
            </section>
          </aside>
        </section>

        <section className="mt-16 grid gap-6 md:grid-cols-3">
          <div className="rounded-2xl border border-surface-200 p-6">
            <h2 className="font-display text-xl font-semibold text-brand-950">Processing</h2>
            <p className="mt-3 text-surface-700">
              Generation begins after successful payment and after the required
              information and evidence are available. Timing can vary; no completion SLA
              is promised.
            </p>
          </div>
          <div className="rounded-2xl border border-surface-200 p-6">
            <h2 className="font-display text-xl font-semibold text-brand-950">
              Refund boundary
            </h2>
            <p className="mt-3 text-surface-700">
              Because this is a personalised digital service, refunds are generally not
              available once document preparation has begun. Technical problems are
              reviewed and a refund may be offered where appropriate. Read the{' '}
              <Link to="/terms" className="font-semibold text-brand-700 underline">terms</Link>.
            </p>
          </div>
          <div className="rounded-2xl border border-surface-200 p-6">
            <h2 className="font-display text-xl font-semibold text-brand-950">
              Payment and data security
            </h2>
            <p className="mt-3 text-surface-700">
              Card details are handled by Stripe; Fixtyer does not store full card
              details. Claim information is handled as described in our{' '}
              <Link to="/privacy" className="font-semibold text-brand-700 underline">privacy policy</Link>.
            </p>
          </div>
        </section>

        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-brand-950">
            Fair ways to prepare a council claim
          </h2>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            <article className="rounded-2xl bg-surface-50 p-6">
              <h3 className="font-semibold text-brand-950">DIY official route</h3>
              <p className="mt-2 text-surface-700">
                DIY via the official authority route can be free, but you prepare,
                organise and submit everything yourself.
              </p>
            </article>
            <article className="rounded-2xl bg-surface-50 p-6">
              <h3 className="font-semibold text-brand-950">Low-cost templates</h3>
              <p className="mt-2 text-surface-700">
                Low-cost templates vary. Compare whether they are generic or
                claim-specific and whether evidence organisation and submission guidance
                are included.
              </p>
            </article>
            <article className="rounded-2xl bg-surface-50 p-6">
              <h3 className="font-semibold text-brand-950">Fixtyer Council Pack</h3>
              <p className="mt-2 text-surface-700">
                £35 for the current personalised documents, organised evidence and
                authority-name recorded-delivery guidance listed above.
              </p>
            </article>
          </div>
        </section>
      </main>
    </>
  );
}
